import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import { DatePipe, NgOptimizedImage } from '@angular/common';
import {IsGranted} from '../../../../../decorator/is-granted';
import {AuthService} from '../../../../../services/auth/auth.service';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {News} from '../../../../../models/news';
import {NewsService} from '../../../../../services/news/news.service';
import {ImageService} from '../../../../../services/image/image.service';
import {UploadFile} from '../../../../../models/upload-file';
import {UploadFileService} from '../../../../../services/upload-file/upload-file.service';

@Component({
  selector: 'app-news-form',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    FormsModule,
    NgOptimizedImage
],
  providers: [DatePipe],
  templateUrl: './news-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './news-form.component.scss'
})
@IsGranted(['ROLE_API_NEWS_WRITE', 'NEWS_VIEW'])
@MetaTitle('News form')
export class NewsFormComponent implements OnInit {

  news: News | null = null;
  uploadImage: UploadFile | null = null;

  form = new FormGroup({
    title: new FormControl('', [Validators.required, Validators.maxLength(64)]),
    excerpt: new FormControl('', [Validators.required, Validators.maxLength(128)]),
    content: new FormControl('', Validators.required),
    publishedAt: new FormControl<string | null>(null),
  });

  constructor(
    protected imageService: ImageService,
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
    private newsService: NewsService,
    private uploadFileService: UploadFileService,
    private datePipe: DatePipe,
  ) {
  }

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));

    if (id) {
      this.loader.startLoading();
      this.newsService.findOneById(id, {groups: 'image'}).subscribe((news: News) => {
        this.news = news;

        this.form.patchValue({
          title: this.news.title,
          excerpt: this.news.excerpt,
          content: this.news.content,
          publishedAt: this.news.publishedAt ? this.datePipe.transform(this.news.publishedAt, 'yyyy-MM-dd HH:mm') : null,
        });
        this.loader.stopLoading();
      });
    }
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    this.loader.startLoading();

    let params: any = this.form.value;

    if (this.uploadImage) {
      params.image = this.uploadImage;
    }

    if (this.isCreate()) {
      this.newsService.create(params).subscribe(() => {
        this.router.navigateByUrl('/configuration/news').then(() => {
          this.snackbar.success('News has been created.');
        });
      });
    } else if (this.news?.id) {
      this.newsService.update(this.news.id, params).subscribe(() => {
        this.router.navigateByUrl('/configuration/news').then(() => {
          this.snackbar.success('News has been modified.');
        });
      });
    }
  }

  upload(event: Event): void {
    const target: any = event.target;
    const file = target?.files[0];

    this.uploadFileService.upload(file).subscribe((uploadFile: UploadFile) => {
      this.uploadImage = uploadFile;
    });
  }

  isCreate(): boolean {
    return !this.news;
  }
}
